import { API_CONFIG, getApiUrl } from "@/config/apiConfig";
import { PageData } from "../types/builder";
import { getAuthHeaders } from "./authApi";
import { handleResponse } from "./apiHandler";

const STORAGE_KEY = "pagebuilder_pages";

const initializeStorage = async (): Promise<void> => {
  const existing = localStorage.getItem(STORAGE_KEY);
  if (!existing) {
    try {
      const response = await fetch("/data/pages.json");
      const dummyPages = await response.json();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dummyPages));
    } catch (error) {
      console.error("Failed to load dummy data:", error);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    }
  }
};

const getStoredPages = (): PageData[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const saveStoredPages = (pages: PageData[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pages));
};

/**
 * Fetch all pages
 */
export const fetchPages = async (projectId?: string): Promise<PageData[]> => {
  const endpoint = API_CONFIG.endpoints.pages.list.replace(
    ":projectId",
    projectId
  );
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const result = await handleResponse(response, "Fetch Pages");
  const pages = result.value;

  if (!pages) {
    throw new Error("Project not found");
  }

  return pages;
};

/**
 * Fetch single page by ID
 */
export const fetchPage = async (id: string): Promise<PageData> => {
  const endpoint = API_CONFIG.endpoints.pages.get.replace(":pageId", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const result = await handleResponse(response, "Fetch Page");

  const page = {
    ...result.value,
    components: result.value.components == null ? 0 : result.value.components,
  };

  return {
    ...page,
    createdAt: new Date(page.created_at),
    updatedAt: new Date(page.updated_at),
  };
};

/**
 * Create new page
 */
export const createPage = async (
  page: Omit<PageData, "id" | "createdAt" | "updatedAt">
): Promise<PageData> => {
  const endpoint = API_CONFIG.endpoints.pages.create;
  const url = `${getApiUrl(endpoint)}`;

  const newPage: PageData = {
    ...page,
    page_type: page.page_type,
    slug: page.title.toLocaleLowerCase(),
    _id: `page-${Date.now()}`,
    created_at: new Date(),
    updated_at: new Date(),
  };

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(newPage),
  });

  const result = await handleResponse(response, "Create Page");
  return result.value;
};

/**
 * Update existing page
 */
export const updatePage = async (
  id: string,
  updates: Partial<PageData>
): Promise<PageData> => {
  const pages = {
    ...updates,
  };

  const endpoint = API_CONFIG.endpoints.pages.update.replace(":pageId", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(pages),
  });

  const result = await handleResponse(response, "Update Page");
  return result.value;
};

/**
 * Delete page
 */
export const deletePage = async (id: string): Promise<void> => {
  const endpoint = API_CONFIG.endpoints.pages.delete.replace(":pageId", id);
  const url = `${getApiUrl(endpoint)}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  await handleResponse(response, "Delete Page");
};
